
import { useEffect, useState } from "react";
import { MissionCard } from "./MissionCard";
import { useUserRoles } from "@/hooks/useUserRoles";
import { supabase } from "@/integrations/supabase/client";

interface Mission {
  id: string;
  title: string;
  description: string | null;
  category: string;
  status: string | null;
  team_id: string;
  created_at: string;
  start_date: string | null;
  end_date: string | null;
  attachment_url: string | null;
  team_name?: string;
  team_image_url?: string; // Add team_image_url property
}

interface MissionListProps {
  missions: Mission[];
  isLoading: boolean;
  isAdmin: boolean;
  onMissionDelete: () => void;
}

export const MissionList = ({ missions, isLoading, isAdmin, onMissionDelete }: MissionListProps) => {
  const { roles } = useUserRoles();
  const [enhancedMissions, setEnhancedMissions] = useState<Mission[]>(missions);
  
  useEffect(() => {
    // Function to fetch team names and logos for all missions
    const fetchTeamData = async () => {
      try {
        // Create a unique list of team IDs
        const teamIds = [...new Set(missions.map(mission => mission.team_id))];
        
        // Skip if no team IDs or no missions
        if (teamIds.length === 0 || missions.length === 0) {
          setEnhancedMissions(missions);
          return;
        }
        
        // Fetch team names and image URLs for all relevant team IDs at once
        const { data: teams, error } = await supabase
          .from('teams')
          .select('id, name, image_url')
          .in('id', teamIds);
          
        if (error) {
          console.error("Error fetching team data:", error);
          setEnhancedMissions(missions);
          return;
        }
        
        // Create a map of team IDs to team data
        const teamDataMap: { [key: string]: { name: string; image_url?: string } } = {};
        teams?.forEach(team => {
          teamDataMap[team.id] = {
            name: team.name,
            image_url: team.image_url
          };
        });
        
        // Enhance missions with team names and logos
        const missionsWithTeamData = missions.map(mission => ({
          ...mission,
          team_name: teamDataMap[mission.team_id]?.name || "Unknown Team",
          team_image_url: teamDataMap[mission.team_id]?.image_url
        }));
        
        setEnhancedMissions(missionsWithTeamData);
      } catch (error) {
        console.error("Error in fetchTeamData:", error);
        setEnhancedMissions(missions);
      }
    };
    
    fetchTeamData();
  }, [missions]);
  
  if (isLoading) {
    return <p>Loading missions...</p>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {enhancedMissions.map((mission) => (
        <MissionCard 
          key={mission.id} 
          mission={mission} 
          isAdmin={isAdmin}
          onDelete={onMissionDelete}
        />
      ))}
    </div>
  );
};
